import type { UserCardStage } from "@-/dev.improve.haniverse.constants.user-card-stage";
import type { DeckStudyCard } from "@-/dev.improve.haniverse.lib.cards";
import {
  getUserCardStage,
  isUserCardMastered,
} from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import type { DeckCardTile } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section";
import {
  deckStudyCardContentKind,
  deckStudyCardPinyin,
  deckStudyCardSubject,
  deckStudyCardTileType,
} from "@-/dev.improve.haniverse.routes.nav.app.deck._lib.study-card-core";
import {
  localDayKey,
  startOfLocalDay,
  startOfNextLocalDay,
} from "@-/dev.improve.haniverse.lib.time.local-day";

const HOUR_MS = 60 * 60 * 1000;

export type DeckUpcomingTimeframe = "day" | "week" | "month";

/** What one card adds to a bucket: a glyph tile under its front, or (a quiz) only a count. */
type ScheduleEntry =
  | { front: string; kind: "tile"; tile: DeckCardTile }
  | { kind: "quiz"; userCardId: string };

/**
 * An explicit selection: one chart bar, or (with `endDate`) consecutive bars of the same size,
 * from the bucket starting at `date` until the exclusive `endDate`.
 */
export type DeckUpcomingSelection =
  | "overdue"
  | "unscheduled"
  | { date: Date; endDate?: Date; hour?: number };

export type DeckUpcomingPeriod = {
  /** Every card in the bucket, quizzes included: the bar's height. */
  cardCount: number;
  date: Date;
  endDate: Date;
  fronts: string[];
  hour?: number;
  key: string;
  /**
   * Quiz cards counted in the bar (only with "Show quiz cards in stats" on). A quiz has no glyph,
   * so it has no tile under `fronts`.
   */
  quizCount: number;
  /** Per-card stage counts; they sum to `cardCount` and stack the chart bar. */
  stageCounts: Record<UserCardStage, number>;
  tilesByFront: Map<string, DeckCardTile[]>;
};

/** Whether a bar lies inside an explicit selection of bars of its own size. */
export function isDeckUpcomingPeriodSelected(
  period: Pick<DeckUpcomingPeriod, "date" | "endDate" | "hour">,
  selection: Pick<DeckUpcomingPeriod, "date" | "endDate" | "hour"> | undefined,
) {
  return (
    selection !== undefined &&
    (period.hour === undefined) === (selection.hour === undefined) &&
    period.date >= selection.date &&
    period.endDate <= selection.endDate
  );
}

function createPeriod(date: Date, hour?: number): DeckUpcomingPeriod {
  return {
    cardCount: 0,
    date,
    endDate: hour === undefined ? startOfNextLocalDay(date) : new Date(date.getTime() + HOUR_MS),
    fronts: [],
    hour,
    key: hour === undefined ? localDayKey(date) : `${localDayKey(date)}-${date.getTime()}`,
    quizCount: 0,
    stageCounts: { familiar: 0, mastered: 0, new: 0, proficient: 0 },
    tilesByFront: new Map(),
  };
}

/**
 * One movable review timeline: past buckets contain cards by their most recent review,
 * matching the former Past view; today and future buckets contain scheduled reviews.
 * Windows span 24 hourly intervals, seven days, or a calendar month.
 * Hour keys use instants so repeated hours at daylight-saving transitions remain distinct.
 * Overdue reviews join today's daily bar, but stay separate from exact hourly buckets.
 * An explicit selection is populated independently of the visible window.
 * Without a selection, all cards in the visible window are shown.
 */
export function buildDeckUpcomingSchedule(
  studyCards: readonly DeckStudyCard[],
  now: Date,
  {
    date = now,
    includeQuizCards = false,
    periodCount,
    rangeStart: visibleStart,
    selection,
    timeframe = "week",
  }: {
    date?: Date;
    /** Count quiz cards in the bars ("Show quiz cards in stats"); otherwise they are left out. */
    includeQuizCards?: boolean;
    /** Fixed-size adjacent windows used while the graph pans between calendar-month views. */
    periodCount?: number;
    rangeStart?: Date;
    selection?: DeckUpcomingSelection;
    timeframe?: DeckUpcomingTimeframe;
  } = {},
) {
  const today = startOfLocalDay(now);
  const todayKey = localDayKey(today);
  const rangeStart = visibleStart === undefined ? startOfLocalDay(date) : new Date(visibleStart);
  if (timeframe === "month" && visibleStart === undefined) {
    rangeStart.setDate(1);
  }
  const rangeEnd =
    periodCount !== undefined
      ? shiftDeckUpcomingRange(rangeStart, timeframe, periodCount)
      : timeframe === "month"
        ? shiftDeckUpcomingMonth(rangeStart, 1)
        : shiftDeckUpcomingRange(rangeStart, timeframe, timeframe === "day" ? 24 : 7);
  const periodsByKey = new Map<string, DeckUpcomingPeriod>();
  if (timeframe === "day") {
    for (let hour = 0; hour < (periodCount ?? 24); hour += 1) {
      const start = new Date(rangeStart.getTime() + hour * HOUR_MS);
      const period = createPeriod(start, start.getHours());
      periodsByKey.set(period.key, period);
    }
  } else {
    for (const day = new Date(rangeStart); day < rangeEnd; day.setDate(day.getDate() + 1)) {
      const period = createPeriod(new Date(day));
      periodsByKey.set(period.key, period);
    }
  }
  const unscheduled = createPeriod(today);
  const overdue = createPeriod(today);
  const periods = [...periodsByKey.values()];
  const selectedPeriod =
    selection === undefined
      ? { ...createPeriod(rangeStart), endDate: rangeEnd, key: "range" }
      : typeof selection === "object"
        ? createSelectedPeriod(selection)
        : createPeriod(today);
  // Daily selections match by calendar day, so overdue reviews join today as they do in its bar.
  const selectedStartKey = localDayKey(selectedPeriod.date);
  const selectedEndKey = localDayKey(selectedPeriod.endDate);
  const dailyCardCounts = new Map<string, number>();
  let maximumCardCount = 0;

  // Per target, the quizzes already counted: a quiz has no tile list to dedupe against.
  const countedQuizIdsByTarget = new Map<DeckUpcomingPeriod, Set<string>>();
  function addCard(target: DeckUpcomingPeriod, entry: ScheduleEntry, stage: UserCardStage) {
    if (entry.kind === "quiz") {
      let counted = countedQuizIdsByTarget.get(target);
      if (counted === undefined) {
        counted = new Set();
        countedQuizIdsByTarget.set(target, counted);
      }
      if (counted.has(entry.userCardId)) {
        return;
      }
      counted.add(entry.userCardId);
      target.quizCount += 1;
      target.cardCount += 1;
      target.stageCounts[stage] += 1;
      return;
    }
    const { front, tile } = entry;
    let tiles = target.tilesByFront.get(front);
    if (tiles === undefined) {
      tiles = [];
      target.tilesByFront.set(front, tiles);
      target.fronts.push(front);
    }
    // A window spanning past and future can include both dates of the same card.
    // Its bars count each date, while the cards surface lists that card only once.
    if (tiles.some((existing) => existing.userCardId === tile.userCardId)) {
      return;
    }
    tiles.push(tile);
    target.cardCount += 1;
    target.stageCounts[stage] += 1;
  }

  for (const studyCard of studyCards) {
    let entryFor: (due: string | null, isMastered?: boolean) => ScheduleEntry;
    if (studyCard.userQuizCard == null) {
      const front = deckStudyCardSubject(studyCard)?.text;
      const cardType = deckStudyCardTileType(studyCard);
      const contentKind = deckStudyCardContentKind(studyCard);
      if (front === undefined || cardType === undefined || contentKind === null) {
        continue;
      }
      entryFor = (due, isMastered) => ({
        front,
        kind: "tile",
        tile: {
          cardType,
          contentKind,
          due,
          ...(isMastered === undefined ? {} : { isMastered }),
          pinyin: deckStudyCardPinyin(studyCard),
          userCardId: studyCard.id,
        },
      });
    } else if (includeQuizCards) {
      // A quiz counts in the bars but has no glyph tile (see `addCard`).
      entryFor = () => ({ kind: "quiz", userCardId: studyCard.id });
    } else {
      continue;
    }
    const fsrs = studyCard.fsrsCard;
    const isMastered = isUserCardMastered({
      lastReview: fsrs?.lastReview ?? null,
      manuallyMasteredAt: studyCard.manuallyMasteredAt,
      scheduledDays: fsrs?.scheduledDays ?? null,
    });
    const stage = getUserCardStage(studyCard);
    const lastReview = fsrs?.lastReview;
    if (lastReview != null) {
      const reviewedAt = new Date(lastReview);
      if (Number.isFinite(reviewedAt.getTime()) && reviewedAt < today) {
        const reviewedKey = localDayKey(reviewedAt);
        const entry = entryFor(fsrs?.due ?? null, isMastered);
        const dailyCount = (dailyCardCounts.get(reviewedKey) ?? 0) + 1;
        dailyCardCounts.set(reviewedKey, dailyCount);
        maximumCardCount = Math.max(maximumCardCount, dailyCount);
        const target =
          timeframe === "day"
            ? periods[Math.floor((reviewedAt.getTime() - rangeStart.getTime()) / HOUR_MS)]
            : periodsByKey.get(reviewedKey);
        if (target !== undefined) {
          addCard(target, entry, stage);
          if (selection === undefined) {
            addCard(selectedPeriod, entry, stage);
          }
        }
        if (
          typeof selection === "object" &&
          (selectedPeriod.hour === undefined
            ? reviewedKey >= selectedStartKey && reviewedKey < selectedEndKey
            : reviewedAt >= selectedPeriod.date && reviewedAt < selectedPeriod.endDate)
        ) {
          addCard(selectedPeriod, entry, stage);
        }
      }
    }
    // Mastered cards are available in the Mastered view; they have no upcoming review.
    if (isMastered) {
      continue;
    }
    const due = fsrs?.due ?? null;
    const dueDate = due === null ? null : new Date(due);
    const isScheduled = dueDate !== null && Number.isFinite(dueDate.getTime());
    const entry = entryFor(isScheduled ? due : null);
    if (isScheduled) {
      const isOverdue = dueDate < today;
      if (isOverdue) {
        addCard(overdue, entry, stage);
      }
      const dailyKey = isOverdue ? todayKey : localDayKey(dueDate);
      const dailyCount = (dailyCardCounts.get(dailyKey) ?? 0) + 1;
      dailyCardCounts.set(dailyKey, dailyCount);
      // Use one daily scale across every window and hourly/daily zoom level.
      maximumCardCount = Math.max(maximumCardCount, dailyCount);
      const target =
        timeframe === "day"
          ? isOverdue
            ? undefined
            : periods[Math.floor((dueDate.getTime() - rangeStart.getTime()) / HOUR_MS)]
          : periodsByKey.get(dailyKey);
      if (target !== undefined) {
        addCard(target, entry, stage);
        if (selection === undefined) {
          addCard(selectedPeriod, entry, stage);
        }
      }
      if (
        selection !== undefined &&
        (selectedPeriod.hour === undefined
          ? dailyKey >= selectedStartKey && dailyKey < selectedEndKey
          : !isOverdue && dueDate >= selectedPeriod.date && dueDate < selectedPeriod.endDate)
      ) {
        addCard(selectedPeriod, entry, stage);
      }
    } else {
      addCard(unscheduled, entry, stage);
    }
  }
  for (const target of [...periodsByKey.values(), unscheduled, overdue, selectedPeriod]) {
    for (const tiles of target.tilesByFront.values()) {
      tiles.sort((a, b) => (a.cardType === b.cardType ? 0 : a.cardType === "meaning" ? -1 : 1));
    }
  }
  return {
    maximumCardCount,
    overdue,
    periods,
    rangeEnd,
    rangeStart,
    selected:
      selection === "unscheduled"
        ? unscheduled
        : selection === "overdue"
          ? overdue
          : selectedPeriod,
    todayKey,
    unscheduled,
  };
}

function createSelectedPeriod(selection: { date: Date; endDate?: Date; hour?: number }) {
  const period = createPeriod(new Date(selection.date), selection.hour);
  if (selection.endDate === undefined || selection.endDate.getTime() === period.endDate.getTime()) {
    return period;
  }
  return {
    ...period,
    endDate: new Date(selection.endDate),
    key: `${period.key}~${selection.endDate.getTime()}`,
  };
}

export function deckUpcomingHourLabel(period: Pick<DeckUpcomingPeriod, "date" | "endDate">) {
  const endHour =
    period.endDate.getHours() === 0 && localDayKey(period.date) !== localDayKey(period.endDate)
      ? 24
      : period.endDate.getHours();
  return `${String(period.date.getHours()).padStart(2, "0")}:00 – ${String(endHour).padStart(2, "0")}:00`;
}

export function shiftDeckUpcomingRange(
  date: Date,
  timeframe: DeckUpcomingTimeframe,
  buckets: number,
) {
  // The chart also snaps a released pan to the next window on the UI thread.
  "worklet";
  if (timeframe === "day") {
    return new Date(date.getTime() + buckets * HOUR_MS);
  }
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + buckets);
  return shifted;
}

export function shiftDeckUpcomingMonth(date: Date, months: number) {
  const shifted = new Date(date);
  shifted.setDate(1);
  shifted.setMonth(shifted.getMonth() + months);
  const lastDay = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0).getDate();
  shifted.setDate(Math.min(date.getDate(), lastDay));
  return shifted;
}

export function deckUpcomingRangeLabel(start: Date, end: Date, timeframe?: DeckUpcomingTimeframe) {
  const isWholeDays =
    start.getTime() === startOfLocalDay(start).getTime() &&
    end.getTime() === startOfLocalDay(end).getTime();
  if (timeframe !== "day" && isWholeDays) {
    // Window ends are exclusive; show the last included calendar day, without midnight times.
    const last = new Date(end.getTime() - 1);
    if (localDayKey(start) === localDayKey(last)) {
      return start.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }
    if (start.getFullYear() === last.getFullYear() && start.getMonth() === last.getMonth()) {
      return `${start.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${last.getDate()}, ${last.getFullYear()}`;
    }
    return `${start.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: start.getFullYear() === last.getFullYear() ? undefined : "numeric",
    })} – ${last.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  }
  const options = {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    year: "numeric",
  } as const;
  return `${start.toLocaleString(undefined, options)} – ${end.toLocaleString(undefined, options)}`;
}

/** The context shared by the more specific dates or hours beneath each chart bar. */
export function deckUpcomingChartRangeLabel(
  start: Date,
  end: Date,
  timeframe: DeckUpcomingTimeframe,
) {
  const last = new Date(end.getTime() - 1);
  if (timeframe === "day") {
    return deckUpcomingRangeLabel(startOfLocalDay(start), startOfNextLocalDay(last));
  }
  const month = (date: Date, year: boolean) =>
    date.toLocaleDateString(undefined, { month: "long", year: year ? "numeric" : undefined });
  if (start.getFullYear() === last.getFullYear()) {
    return start.getMonth() === last.getMonth()
      ? month(start, true)
      : `${month(start, false)} – ${month(last, true)}`;
  }
  return `${month(start, true)} – ${month(last, true)}`;
}
